import { Card, CardContent, Box, Typography, Stack, Avatar, Grid, Chip } from "@mui/material";
import {
  Warning as WarningIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

interface RiskStats {
  total: number;
  active: number;
  investigating: number;
  acknowledged: number;
  resolved: number;
  closed: number;
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

interface RiskStatsCardsProps {
  stats: RiskStats;
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: "error" | "warning" | "info" | "success";
  subtitle?: string;
}

const StatCard = ({ title, value, icon, color, subtitle }: StatCardProps) => (
  <Card variant="outlined">
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5">{value}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: `${color}.main`, color: `${color}.contrastText`, width: 36, height: 36 }}>
          {icon}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

const severityColors = {
  critical: "error",
  high: "warning",
  medium: "warning",
  low: "success",
} as const;

export const RiskStatsCards = ({ stats }: RiskStatsCardsProps) => {
  return (
    <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 2 }}>
      {/* Severity chips - compact inline display */}
      <Card variant="outlined" sx={{ minWidth: 200 }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            By Severity
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {[
              { label: "Critical", value: stats.bySeverity.critical, color: severityColors.critical },
              { label: "High", value: stats.bySeverity.high, color: severityColors.high },
              { label: "Medium", value: stats.bySeverity.medium, color: severityColors.medium },
              { label: "Low", value: stats.bySeverity.low, color: severityColors.low },
            ].map((item) => (
              <Chip
                key={item.label}
                label={`${item.value} ${item.label}`}
                size="small"
                color={item.color}
                variant="outlined"
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Stat cards in a row */}
      <Box sx={{ display: "flex", gap: 2, flex: 1, minWidth: 0 }}>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <StatCard
            title="Active"
            value={stats.active}
            icon={<WarningIcon fontSize="small" />}
            color="error"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <StatCard
            title="Investigating"
            value={stats.investigating}
            icon={<SearchIcon fontSize="small" />}
            color="warning"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <StatCard
            title="Acknowledged"
            value={stats.acknowledged}
            icon={<VisibilityIcon fontSize="small" />}
            color="info"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <StatCard
            title="Resolved (24h)"
            value={stats.resolved}
            icon={<CheckCircleIcon fontSize="small" />}
            color="success"
          />
        </Box>
      </Box>
    </Stack>
  );
};
